import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, CreditCard, Calendar, AlertCircle, FileText, Heart } from 'lucide-react';

const Terms = () => {
  return (
    <div className="py-16" data-testid="terms-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center space-x-2 text-maroon hover:text-terracotta mb-8">
          <ArrowLeft size={20} />
          <span className="font-semibold">Back to Home</span>
        </Link>

        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-heading font-bold text-maroon mb-4">
            Terms & Conditions
          </h1>
          <p className="text-lg text-maroon/70">
            Please read these terms carefully before booking with Chimba Travel
          </p>
        </div>

        {/* Refund Policy Section */}
        <div className="card mb-8" id="refund-policy">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-terracotta/10 rounded-full">
              <CreditCard className="text-terracotta" size={24} />
            </div>
            <h2 className="text-2xl font-heading font-bold text-maroon">Refund Policy</h2>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="font-bold text-maroon mb-2">5% Deposit Terms</h3>
              <p className="text-maroon/80">
                A <strong>non-refundable deposit of 5%</strong> of the total package price is required to secure your booking. 
                This covers initial administrative costs and vendor deposits.
              </p>
            </div>

            <div>
              <h3 className="font-bold text-maroon mb-3">Cancellation Deadlines</h3>
              <div className="bg-sand/30 rounded-sm overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-maroon text-white">
                      <th className="px-4 py-3 text-left">Timing</th>
                      <th className="px-4 py-3 text-left">Refund</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-maroon/10">
                    <tr>
                      <td className="px-4 py-3 text-maroon font-semibold">60+ days before departure</td>
                      <td className="px-4 py-3 text-maroon/80">Full refund of the balance (excluding the 5% deposit)</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-maroon font-semibold">30–59 days before departure</td>
                      <td className="px-4 py-3 text-maroon/80">50% refund of the total package price</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-maroon font-semibold">Less than 30 days before departure</td>
                      <td className="px-4 py-3 text-terracotta font-semibold">No refund available</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h3 className="font-bold text-maroon mb-2">Refund Processing Time</h3>
              <p className="text-maroon/80">
                Approved refunds will be processed within <strong>7–10 business days</strong> via the original payment method.
              </p>
            </div>

            <div className="bg-terracotta/10 p-4 rounded-sm border-l-4 border-terracotta">
              <h3 className="font-bold text-maroon mb-2 flex items-center gap-2">
                <Calendar size={18} />
                Balance Payment Timeline
              </h3>
              <p className="text-maroon/80">
                The remaining <strong>95% balance</strong> is due no later than <strong>60 days prior</strong> to the scheduled departure date.
              </p>
            </div>
          </div>
        </div>

        {/* Booking Terms Section */}
        <div className="card mb-8" id="booking-terms">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-terracotta/10 rounded-full">
              <FileText className="text-terracotta" size={24} />
            </div>
            <h2 className="text-2xl font-heading font-bold text-maroon">Booking Terms</h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="font-bold text-maroon mb-2">Booking Confirmation</h3>
              <p className="text-maroon/80">
                A booking is confirmed only when Chimba Travel issues a <strong>written confirmation email</strong> following 
                the receipt of the initial 5% deposit.
              </p>
            </div>

            <div>
              <h3 className="font-bold text-maroon mb-2">Payment Terms</h3>
              <p className="text-maroon/80">
                Prices are subject to change until the 5% deposit is paid. Once the deposit is received, 
                <strong> your package price is locked in</strong>.
              </p>
            </div>

            <div>
              <h3 className="font-bold text-maroon mb-2">Cancellation Policy</h3>
              <p className="text-maroon/80">
                All cancellation requests must be submitted in writing. Chimba Travel reserves the right to cancel a tour 
                due to low enrollment or "Force Majeure," in which case a <strong>full refund or credit</strong> will be offered.
              </p>
            </div>
          </div>
        </div>

        {/* Liability Section */}
        <div className="card mb-8" id="liability">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-terracotta/10 rounded-full">
              <Shield className="text-terracotta" size={24} />
            </div>
            <h2 className="text-2xl font-heading font-bold text-maroon">Liability Limitations</h2>
          </div>

          <p className="text-maroon/80">
            Chimba Travel acts as an agent for third-party providers and is <strong>not liable</strong> for injury, loss, 
            or delays caused by these vendors or events outside our direct control. This includes, but is not limited to:
          </p>
          <ul className="list-disc list-inside text-maroon/80 mt-3 space-y-1">
            <li>Airlines, hotels, and transportation providers</li>
            <li>Local tour operators and guides</li>
            <li>Natural disasters or weather events</li>
            <li>Government actions or travel restrictions</li>
            <li>Civil unrest or security concerns</li>
          </ul>
        </div>

        {/* Customer Responsibilities Section */}
        <div className="card mb-8" id="customer-responsibilities">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-terracotta/10 rounded-full">
              <AlertCircle className="text-terracotta" size={24} />
            </div>
            <h2 className="text-2xl font-heading font-bold text-maroon">Customer Responsibilities</h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="font-bold text-maroon mb-2">Travel Documents</h3>
              <p className="text-maroon/80">
                Travelers must ensure they have:
              </p>
              <ul className="list-disc list-inside text-maroon/80 mt-2 space-y-1">
                <li>Valid passports (minimum <strong>6 months validity</strong> beyond return date)</li>
                <li>Required visas for all destinations</li>
                <li>Necessary vaccinations and health certificates</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-maroon mb-2">Health & Safety Requirements</h3>
              <p className="text-maroon/80">
                Travelers must notify Chimba Travel of any <strong>medical conditions or dietary needs</strong> at the time of booking.
              </p>
            </div>
          </div>
        </div>

        {/* Travel Insurance Section */}
        <div className="card mb-8 border-2 border-terracotta" id="insurance">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-terracotta/10 rounded-full">
              <Heart className="text-terracotta" size={24} />
            </div>
            <h2 className="text-2xl font-heading font-bold text-maroon">Travel Insurance</h2>
          </div>

          <div className="bg-terracotta/10 p-4 rounded-sm">
            <p className="text-maroon font-semibold mb-2">Highly Recommended!</p>
            <p className="text-maroon/80">
              Comprehensive travel insurance is <strong>highly recommended</strong> to protect your investment, 
              especially since the 5% deposit is non-refundable. Travel insurance can cover:
            </p>
            <ul className="list-disc list-inside text-maroon/80 mt-3 space-y-1">
              <li>Trip cancellation and interruption</li>
              <li>Medical emergencies abroad</li>
              <li>Lost or delayed baggage</li>
              <li>Emergency evacuation</li>
            </ul>
          </div>
        </div>

        {/* Last Updated */}
        <div className="text-center text-maroon/60 text-sm">
          <p>Last updated: December 2025</p>
          <p className="mt-2">
            Questions about our terms? Contact us at{' '}
            <a href="mailto:hello@chimbatravel.com" className="text-maroon hover:text-terracotta underline">
              hello@chimbatravel.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Terms;
